import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Download, Copy, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import track from "@/lib/track";
import LoaderOlimpo from "@/components/LoaderOlimpo";
import Markdown from "@/components/Markdown";
import TarifaEditor from "@/components/TarifaEditor";
import useRefetchOnFocus from "@/hooks/useRefetchOnFocus";

const ITEMS = [
  { key: "quien_sos", label: "QUIÉN SOS" },
  { key: "tu_empresa", label: "TU EMPRESA" },
  { key: "herramientas", label: "HERRAMIENTAS QUE USÁS" },
  { key: "dolores", label: "DÓNDE SE TE VA EL TIEMPO" },
  { key: "procesos", label: "TUS PROCESOS" },
];

export default function MiEmpresa() {
  const [md, setMd] = useState("");
  const [nombre, setNombre] = useState("mi-empresa");
  const [desbloqueado, setDesbloqueado] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [cargando, setCargando] = useState(true);
  const [copiado, setCopiado] = useState(false);
  const [vhInfo, setVhInfo] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/cerebro");
      setMd(data.markdown);
      setNombre(data.nombre_empresa);
      setDesbloqueado(!!data.desbloqueado);
      setChecklist(data.checklist || {});
      setCargando(false);
      try { const f = await api.get("/focos"); setVhInfo(f.data.valor_hora_info || null); } catch {}
    } catch (e) { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useRefetchOnFocus(cargar, !cargando);

  const slug = nombre.toLowerCase().replace(/\s+/g, "-");

  const descargar = (ext) => {
    const tipo = ext === "txt" ? "text/plain" : "text/markdown";
    const blob = new Blob([md], { type: tipo });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cerebro-${slug}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copiar = async () => {
    await navigator.clipboard.writeText(md);
    track("cerebro_copiado", { origen: "mi-empresa" });
    setCopiado(true);
    toast.success("Copiado. Cargalo en tu herramienta de IA.");
    setTimeout(() => setCopiado(false), 2200);
  };

  if (cargando) return <LoaderOlimpo />;

  return (
    <div className="max-w-[900px] mx-auto w-full px-6 md:px-8 py-14">
      {vhInfo?.set && (
        <div className="mb-8" data-testid="miempresa-valor-hora">
          <div className="mono-label text-dorado mb-2">EL VALOR DE TU HORA</div>
          <TarifaEditor info={vhInfo} onRecalc={(d) => setVhInfo(d.valor_hora_info)} />
        </div>
      )}
      {desbloqueado ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="titulo text-4xl mb-4" data-testid="cerebro-titulo">El cerebro de tu empresa.</h1>
          <p className="text-gris mb-2 leading-relaxed">
            Todo lo que fuiste armando queda acá. Cargalo en tu herramienta de IA y va a entender tu empresa sin que le expliques nada.
          </p>
          <p className="text-gris mb-8 leading-relaxed" data-testid="cerebro-crece">
            Se actualiza solo. Cada proceso que definís entra acá, y tu IA se vuelve más útil cada vez que volvés.
          </p>
          <div className="flex flex-wrap gap-3 mb-12">
            <button className="btn-dorado flex items-center gap-2" onClick={copiar} data-testid="cerebro-copiar">
              {copiado ? <Check size={15} /> : <Copy size={15} />} {copiado ? "COPIADO" : "COPIAR PARA MI IA"}
            </button>
            <button className="btn-fantasma flex items-center gap-2" onClick={() => descargar("md")} data-testid="cerebro-descargar-md">
              <Download size={15} /> DESCARGAR .MD
            </button>
            <button className="btn-fantasma flex items-center gap-2" onClick={() => descargar("txt")} data-testid="cerebro-descargar-txt">
              <Download size={15} /> DESCARGAR .TXT
            </button>
          </div>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="titulo text-4xl mb-4" data-testid="cerebro-titulo">Tu cerebro se está armando.</h1>
          <p className="text-gris mb-8 leading-relaxed">
            Todo lo que vas definiendo queda acá. Cuando llegues a implementar tu primer proceso, te lo damos para que lo cargues en tu herramienta de IA y trabaje sabiendo todo esto.
          </p>

          <div className="card-dark p-6 mb-8" data-testid="cerebro-checklist">
            {ITEMS.map((it) => {
              const ok = !!checklist[it.key];
              return (
                <div key={it.key} className="flex items-center gap-3 py-2">
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    {ok ? <Check size={16} className="text-dorado" /> : <span className="w-1.5 h-1.5 rounded-full bg-borde" />}
                  </span>
                  <span className="mono-label" style={{ color: ok ? "#C9A84C" : "#666" }}>
                    {it.label}{!ok && it.key === "procesos" ? " — vacío hasta que definas el primero" : ""}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2 mb-12 mono-label text-gris" data-testid="cerebro-bloqueado">
            <Lock size={13} /> SE HABILITA CUANDO IMPLEMENTES TU PRIMER PROCESO
          </div>
        </motion.div>
      )}

      <div className="mb-3 text-sm text-gris leading-relaxed border-l-2 border-dorado/50 pl-3" data-testid="cerebro-aviso-primera">
        Está escrito en primera persona, como si lo dijeras vos. Es a propósito: tu IA lo tiene que leer como si se lo estuvieras contando.
      </div>
      <div className="card-dark p-8">
        <div className="text-crema text-sm leading-relaxed" data-testid="cerebro-contenido"><Markdown>{md}</Markdown></div>
      </div>
    </div>
  );
}
